import React, { useEffect, useMemo, useState, useCallback } from "react";
import { adminApiRequest } from "../utils/adminApi";

const emptyForm = {
  name: "",
  price: "",
  coverage: "",
  is_active: 1,
  features: [""],
};

export default function WeddingPackages() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const sortedPackages = useMemo(() => {
    return [...packages].sort((a, b) => Number(b.id) - Number(a.id));
  }, [packages]);
  

  const getErrorMessage = (err, fallback) => {
    if (err.message === "REQUEST_TIMEOUT") {
      return "Request timed out. Please try again.";
    }
    if (err.message === "NETWORK_ERROR") {
      return "Network error. Please check your connection.";
    }
    if (err.message === "UNAUTHORIZED" || err.message === "SESSION_EXPIRED") {
      return "Your session has expired. Please sign in again.";
    }
    return err.message || fallback;
  };

 const loadPackages = useCallback(async () => {
  try {
    setLoading(true);
    setError("");

    const res = await adminApiRequest({
      script: "list_wedding_packages",
    });

    if (!res?.success) {
      throw new Error(res?.message || "Failed to load wedding packages.");
    }

    setPackages(Array.isArray(res.data) ? res.data : []);
  } catch (err) {
    setError(getErrorMessage(err, "Unable to load wedding packages."));
  } finally {
    setLoading(false);
  }
}, []);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingItem(null);
  };

  const openCreateModal = () => {
    resetForm();
    setError("");
    setSuccess("");
    setIsModalOpen(true);
  };

  const openEditModal = (item) => {
    const features =
      Array.isArray(item.features) && item.features.length > 0
        ? item.features
        : [""];

    setEditingItem(item);
    setForm({
      name: item.name || "",
      price: item.price ?? "",
      coverage: item.coverage || "",
      is_active: Number(item.is_active) === 1 ? 1 : 0,
      features,
    });
    setError("");
    setSuccess("");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const handleChange = (key) => (e) => {
    const value = key === "is_active" ? Number(e.target.value) : e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleFeatureChange = (index, value) => {
    setForm((prev) => ({
      ...prev,
      features: prev.features.map((feature, i) =>
        i === index ? value : feature
      ),
    }));
  };

  const addFeatureRow = () => {
    setForm((prev) => ({
      ...prev,
      features: [...prev.features, ""],
    }));
  };

  const removeFeatureRow = (index) => {
    setForm((prev) => {
      if (prev.features.length === 1) {
        return {
          ...prev,
          features: [""],
        };
      }

      return {
        ...prev,
        features: prev.features.filter((_, i) => i !== index),
      };
    });
  };

  const validateForm = () => {
    if (!form.name.trim()) return "Package name is required.";
    if (form.price === "" || Number(form.price) <= 0)
      return "Enter a valid price.";
    if (!form.coverage.trim()) return "Coverage is required.";

    const cleanedFeatures = form.features
      .map((feature) => feature.trim())
      .filter(Boolean);

    if (cleanedFeatures.length === 0) {
      return "At least one feature is required.";
    }

    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const cleanedFeatures = form.features
        .map((feature) => feature.trim())
        .filter(Boolean);

      const payload = {
        name: form.name.trim(),
        price: Number(form.price),
        coverage: form.coverage.trim(),
        is_active: Number(form.is_active),
        features: cleanedFeatures,
      };

      const res = await adminApiRequest({
        script: editingItem
          ? "update_wedding_package"
          : "create_wedding_package",
        data: editingItem ? { id: editingItem.id, ...payload } : payload,
      });

      if (!res?.success) {
        throw new Error(
          res?.message ||
            `Failed to ${editingItem ? "update" : "create"} wedding package.`
        );
      }

      setSuccess(
        editingItem
          ? "Wedding package updated successfully."
          : "Wedding package created successfully."
      );

      closeModal();
      await loadPackages();
    } catch (err) {
      setError(
        getErrorMessage(
          err,
          `Unable to ${editingItem ? "update" : "create"} wedding package.`
        )
      );
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (item) => {
    const nextStatus = Number(item.is_active) === 1 ? 0 : 1;

    try {
      setError("");
      setSuccess("");

      const res = await adminApiRequest({
        script: "toggle_wedding_package_status",
        data: {
          id: item.id,
          is_active: nextStatus,
        },
      });

      if (!res?.success) {
        throw new Error(res?.message || "Failed to update package status.");
      }

      setSuccess(
        nextStatus === 1
          ? "Wedding package activated successfully."
          : "Wedding package deactivated successfully."
      );

      setPackages((prev) =>
        prev.map((pkg) =>
          Number(pkg.id) === Number(item.id)
            ? { ...pkg, is_active: nextStatus }
            : pkg
        )
      );
    } catch (err) {
      setError(getErrorMessage(err, "Unable to update package status."));
    }
  };

  return (
    <section className="h-full min-h-0">
      <div className="flex h-full min-h-0 flex-col rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-4 border-b border-slate-200 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900">
              Wedding Packages
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Manage wedding package pricing, coverage, and features.
            </p>
          </div>

          <button
            onClick={openCreateModal}
            className="rounded-xl bg-slate-900 px-4 py-3 text-sm font-medium text-white transition hover:bg-slate-800"
          >
            + Add Wedding Package
          </button>
        </div>

        {(error || success) && (
          <div className="px-5 pt-4">
            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {success && (
              <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                {success}
              </div>
            )}
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-auto p-5">
          {loading ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
              Loading wedding packages...
            </div>
          ) : sortedPackages.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center">
              <p className="text-sm text-slate-500">
                No wedding packages added yet.
              </p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-slate-200">
              <div className="overflow-x-auto">
                <table className="min-w-full text-left">
                  <thead className="bg-slate-50">
                    <tr className="text-sm text-slate-600">
                      <th className="px-4 py-3 font-semibold">Package</th>
                      <th className="px-4 py-3 font-semibold">Coverage</th>
                      <th className="px-4 py-3 font-semibold">Features</th>
                      <th className="px-4 py-3 font-semibold">Price</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold text-right">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-200 bg-white">
                    {sortedPackages.map((item) => (
                      <tr key={item.id} className="align-top">
                        <td className="px-4 py-4">
                          <div className="font-medium text-slate-900">
                            {item.name}
                          </div>
                        </td>

                        <td className="px-4 py-4 text-sm text-slate-700">
                          {item.coverage}
                        </td>

                        <td className="px-4 py-4">
                          {Array.isArray(item.features) && item.features.length > 0 ? (
                            <div className="space-y-1 text-sm text-slate-700">
                              {item.features.map((feature, index) => (
                                <div key={`${item.id}-${index}`}>• {feature}</div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-sm text-slate-400">—</span>
                          )}
                        </td>

                        <td className="px-4 py-4 text-sm font-medium text-slate-900">
                          £{Number(item.price).toFixed(2)}
                        </td>

                        <td className="px-4 py-4">
                          <span
                            className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                              Number(item.is_active) === 1
                                ? "bg-green-100 text-green-700"
                                : "bg-slate-200 text-slate-600"
                            }`}
                          >
                            {Number(item.is_active) === 1 ? "Active" : "Inactive"}
                          </span>
                        </td>

                       
                        <td className="px-4 py-4">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => openEditModal(item)}
                              className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                            >
                              Edit
                            </button>

                            <button
                              onClick={() => handleToggleStatus(item)}
                              className={`rounded-lg px-3 py-2 text-sm font-medium transition ${
                                Number(item.is_active) === 1
                                  ? "border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"
                                  : "border border-green-300 bg-green-50 text-green-700 hover:bg-green-100"
                              }`}
                            >
                              {Number(item.is_active) === 1
                                ? "Deactivate"
                                : "Activate"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-3xl rounded-3xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <div>
                <h3 className="text-xl font-semibold text-slate-900">
                  {editingItem ? "Edit Wedding Package" : "Add Wedding Package"}
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  Fill in the package details below.
                </p>
              </div>

              <button
                onClick={closeModal}
                className="rounded-lg px-3 py-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Package Name
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={handleChange("name")}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-slate-900"
                    placeholder="e.g. Package 1"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Price (£)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.price}
                    onChange={handleChange("price")}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-slate-900"
                    placeholder="e.g. 700"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Coverage
                  </label>
                  <input
                    type="text"
                    value={form.coverage}
                    onChange={handleChange("coverage")}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-slate-900"
                    placeholder="e.g. Full day, 5 hours"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Status
                  </label>
                  <select
                    value={form.is_active}
                    onChange={handleChange("is_active")}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-slate-900"
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <label className="block text-sm font-medium text-slate-700">
                      Features
                    </label>

                    <button
                      type="button"
                      onClick={addFeatureRow}
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                    >
                      + Add Feature
                    </button>
                  </div>

                  <div className="space-y-3">
                    {form.features.map((feature, index) => (
                      <div key={index} className="flex gap-3">
                        <input
                          type="text"
                          value={feature}
                          onChange={(e) =>
                            handleFeatureChange(index, e.target.value)
                          }
                          className="flex-1 rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-slate-900"
                          placeholder={`Feature ${index + 1}`}
                        />

                        <button
                          type="button"
                          onClick={() => removeFeatureRow(index)}
                          className="rounded-xl border border-red-200 px-4 py-3 text-sm font-medium text-red-600 transition hover:bg-red-50"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-end gap-3 border-t border-slate-200 pt-5">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-xl border border-slate-300 px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-xl bg-slate-900 px-5 py-3 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving
                    ? editingItem
                      ? "Updating..."
                      : "Saving..."
                    : editingItem
                    ? "Update Package"
                    : "Create Package"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}